import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Send, Square, Paperclip, X, Upload, FolderOpen, Loader2, Check, Search, Clock3, Trash2, CornerDownLeft, FileVideo, Crosshair, Image, Music, Film, Sparkles, Volume2, Play, Pause } from 'lucide-react';
import { getCategorizedLibraryAssets, type AssetScope, type CategorizedAssetItem, type LibraryCategory } from '@/api/modules/assets';
import { AssetImage } from '@/components/asset-image';
import { cn } from '@/utils/cn';
import { clearSessionDraft, loadSessionDraft, saveSessionDraft } from '@/utils/session-draft-cache';
import { HONGBAO_EFFECT_PROMPT } from '@/data/gameplay-prompts';
import type { PendingSend } from '@/utils/session-send-queue';
import type { PreviewSelection } from '@/types/preview-selection';

interface UploadedFile { file: File; preview?: string; }

function QueuedAttachmentThumbnail({ item }: { item: PendingSend }): JSX.Element {
  const firstFile = item.files[0];
  const libraryAsset = item.libraryAssets[0];
  const [localPreview, setLocalPreview] = useState<string>();

  useEffect(() => {
    if (!firstFile?.type.startsWith('image/')) {
      setLocalPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(firstFile);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [firstFile]);

  if (localPreview) return <img src={localPreview} alt="待发送附件" className="h-full w-full object-cover" />;
  // 音频资源无缩略图，用音乐图标占位（AssetImage 会把音频 URL 当图片加载失败）
  if (libraryAsset?.type === 'audio') return <Music size={14} className="text-primary" />;
  if (libraryAsset) return <AssetImage src={libraryAsset.previewUrl ?? libraryAsset.url} alt={libraryAsset.name} className="h-full w-full" />;
  if (firstFile?.type.startsWith('video/')) return <FileVideo size={14} className="text-neon-cyan" />;
  return <Paperclip size={13} className="text-muted-foreground" />;
}

// 与真实资产库（Materials 页）对齐：公共/个人双 scope + 同名类目
const LIBRARY_SCOPE_TABS: Array<{ key: AssetScope; label: string }> = [
  { key: 'public', label: '公共资产库' },
  { key: 'personal', label: '我的资产库' },
];
const PUBLIC_LIBRARY_TABS: Array<{ key: LibraryCategory; label: string; icon: typeof Image }> = [
  { key: 'image', label: '图片', icon: Image },
  { key: 'particle', label: '粒子', icon: Sparkles },
  { key: 'animation', label: '动图', icon: Film },
  { key: 'soundEffect', label: '音效', icon: Volume2 },
  { key: 'bgm', label: '背景音乐', icon: Music },
];
const PERSONAL_LIBRARY_TABS: Array<{ key: LibraryCategory; label: string; icon: typeof Image }> = [
  { key: 'image', label: '图片', icon: Image },
  { key: 'animation', label: '动图', icon: Film },
  { key: 'audio', label: '音频', icon: Music },
];
const LIBRARY_PAGE = 40;

interface ChatInputProps {
  onSend: (text: string, files?: File[], libraryAssets?: CategorizedAssetItem[], previewSelection?: PreviewSelection) => void | Promise<void>;
  onStop?: () => void;
  queuedItems?: PendingSend[];
  onRemoveQueued?: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
  /** 生成流水线/微调图执行中：消息只进入待发送区，发送位显示执行态。 */
  processing?: boolean;
  processingLabel?: string;
  /** 流式生成进行中（从发送到 done/abort）；期间新消息进入待发送队列。 */
  streaming?: boolean;
  /**
   * 外部填充输入框（不自动发送）；nonce 变化时重新写入，支持重复点击同一快捷指令。
   * mode（BUG-20260820-WEB-002）：默认追加；'append-hongbao' 仅当已有内容且未含特效层时追加；
   * 'remove-hongbao' 剥离已追加的特效层与 [NO_EARN_FX] 标记。
   */
  prefill?: { text: string; nonce: number; mode?: 'append-hongbao' | 'remove-hongbao' };
  /** 当前会话 ID，用于资产库关联。 */
  sessionId?: string | null;
  targetSelection?: PreviewSelection | null;
  onClearTargetSelection?: () => void;
  onRestoreTargetSelection?: (selection: PreviewSelection | null) => void;
}

export function ChatInput({
  onSend, onStop, queuedItems = [], onRemoveQueued, disabled = false, loading = false,
  processing = false, processingLabel, streaming = false, prefill, sessionId,
  targetSelection, onClearTargetSelection, onRestoreTargetSelection,
}: ChatInputProps) {
  const busy = loading || streaming;
  const [text, setText] = useState('');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // 资产库 state
  const [libScope, setLibScope] = useState<AssetScope>('public');
  const [libTab, setLibTab] = useState('image');
  /** 正在试听的音频条目 id（音效/BGM/个人音频卡片播放态） */
  const [libPlayingId, setLibPlayingId] = useState<string | null>(null);
  const [libKeyword, setLibKeyword] = useState('');
  const [libLoading, setLibLoading] = useState(false);
  const [libItems, setLibItems] = useState<CategorizedAssetItem[]>([]);
  const [libSelected, setLibSelected] = useState<CategorizedAssetItem[]>([]);
  const [libTotal, setLibTotal] = useState(0);
  const [libSubcategory, setLibSubcategory] = useState('');
  const [libSubcategories, setLibSubcategories] = useState<string[]>([]);
  const libOffsetRef = useRef(0);
  const libLoadedRef = useRef(0);
  const libDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef({ text: '', files: [] as UploadedFile[], libraryAssets: [] as CategorizedAssetItem[] });
  const draftSessionRef = useRef<string | null>(null);
  const draftLoadSeqRef = useRef(0);
  const restoringDraftRef = useRef(false);

  draftRef.current = { text, files, libraryAssets: libSelected };

  // 输入草稿与 Blob 附件按会话保存到 IndexedDB，切换回来可继续编辑。
  useEffect(() => {
    const previousSessionId = draftSessionRef.current;
    if (previousSessionId && previousSessionId !== sessionId) {
      void saveSessionDraft(previousSessionId, draftRef.current);
    }
    draftSessionRef.current = sessionId ?? null;
    const seq = ++draftLoadSeqRef.current;
    restoringDraftRef.current = true;
    setText('');
    setFiles((current) => {
      current.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
      return [];
    });
    setLibSelected([]);
    if (!sessionId) {
      restoringDraftRef.current = false;
      return;
    }
    void loadSessionDraft(sessionId).then((draft) => {
      if (seq !== draftLoadSeqRef.current || draftSessionRef.current !== sessionId) {
        draft?.files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
        return;
      }
      setText(draft?.text ?? '');
      setFiles(draft?.files ?? []);
      setLibSelected(draft?.libraryAssets ?? []);
      restoringDraftRef.current = false;
    });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || restoringDraftRef.current) return;
    const timer = setTimeout(() => {
      void saveSessionDraft(sessionId, { text, files, libraryAssets: libSelected });
    }, 250);
    return () => clearTimeout(timer);
  }, [sessionId, text, files, libSelected]);

  useEffect(() => () => {
    const currentSessionId = draftSessionRef.current;
    if (currentSessionId) void saveSessionDraft(currentSessionId, draftRef.current);
    draftRef.current.files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
  }, []);

  // 快捷指令填入：nonce 变化时追加到末尾（不覆盖已有内容），并聚焦（不发送）。
  useEffect(() => {
    if (!prefill) return;
    if (prefill.mode === 'append-hongbao') {
      // BUG-20260820-WEB-002: 先选快捷指令后开开关 → 末尾追加特效层；空输入或已含则跳过
      setText(prev => (prev && !prev.includes('【网赚红包特效层】')) ? `${prev}\n${prefill.text}` : prev);
      return;
    }
    if (prefill.mode === 'remove-hongbao') {
      // BUG-20260820-WEB-002: 关开关 → 移除已追加的特效层与抑制标记；
      // 内置红包变体内嵌层与常量不完全相同，split 不会误删
      setText(prev => prev.split(HONGBAO_EFFECT_PROMPT)[0].replace(/\s*\[NO_EARN_FX\]/g, '').trimEnd());
      return;
    }
    // DO-NOT-REVERT: BUG-20260909-PW-004 普通快捷指令 prefill = 用户显式输入——先作废在途的草稿恢复
    // （否则 sessionDraft 异步返回晚于 prefill 时会清空指令文本，初次生成缺失手势/文字/粒子的诱因之一）
    // （loadSessionDraft 异步回调晚到时 setText('') 会覆盖刚填入的指令文本；实测视频转试玩
    // 指令正文被清成只剩 [NO_EARN_FX]，AI 端因消息体不含文字/粒子/手势要求而不生成对应效果）
    draftLoadSeqRef.current += 1;
    restoringDraftRef.current = false;
    setText(prev => prev ? `${prev}\n${prefill.text}` : prefill.text);
    taRef.current?.focus();
  }, [prefill?.nonce]);

  useEffect(() => {
    if (!targetSelection) return;
    requestAnimationFrame(() => taRef.current?.focus());
  }, [targetSelection?.id]);

  // 资产库数据加载（scope/category/子类目/搜索参数与 Materials 页对齐）
  const fetchLibrary = useCallback(async (scope: AssetScope, tab: string, kw: string, sub: string, loadMore = false) => {
    setLibLoading(true);
    try {
      const offset = loadMore ? libOffsetRef.current : 0;
      const tabs = scope === 'public' ? PUBLIC_LIBRARY_TABS : PERSONAL_LIBRARY_TABS;
      const activeCategory = tabs.find((t) => t.key === tab)?.key ?? 'image';
      const { items: newItems, total: newTotal, subcategories: newSubs } = await getCategorizedLibraryAssets({
        scope, category: activeCategory,
        subcategory: scope === 'public' ? (sub || undefined) : undefined,
        search: kw, limit: LIBRARY_PAGE, offset,
      });
      if (!loadMore) setLibSubcategories(newSubs);
      if (loadMore) {
        setLibItems((prev) => [...prev, ...newItems]);
        libLoadedRef.current += newItems.length;
      } else {
        setLibItems(newItems);
        libLoadedRef.current = newItems.length;
      }
      setLibTotal(newTotal);
      libOffsetRef.current = offset + newItems.length;
    } catch {
      if (!loadMore) { setLibItems([]); libLoadedRef.current = 0; setLibSubcategories([]); }
      setLibTotal(0);
    } finally {
      setLibLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showLibrary) {
      libOffsetRef.current = 0;
      libLoadedRef.current = 0;
      setLibSubcategory('');
      setLibSelected([]);
      setLibKeyword('');
      setLibPlayingId(null);
      fetchLibrary(libScope, libTab, '', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLibrary, libTab, libScope]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0 && libSelected.length === 0) || disabled) return;
    const selectedFiles = files.map(f => f.file);
    const selectedAssets = [...libSelected];
    void onSend(
      trimmed,
      selectedFiles.length > 0 ? selectedFiles : undefined,
      selectedAssets.length > 0 ? selectedAssets : undefined,
      targetSelection ?? undefined,
    );
    setText('');
    files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
    setFiles([]);
    setLibSelected([]);
    onClearTargetSelection?.();
    if (sessionId) void clearSessionDraft(sessionId);
  }, [text, files, libSelected, disabled, onSend, sessionId, targetSelection, onClearTargetSelection]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    // DO-NOT-REVERT: BUG-20260812-009 — 中文输入法回车确认候选词不得触发发送（isComposing / keyCode 229），
    // 否则确认候选的回车会把半截串直接发出（用户实测：中文打字切英文回车发出乱码指令）
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const addFiles = (selected: FileList) => {
    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < selected.length; i++) {
      const f = selected[i];
      newFiles.push({
        file: f,
        preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      });
    }
    setFiles(prev => [...prev, ...newFiles]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles(prev => {
      const f = prev[idx];
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removeLibAsset = (id: string) => {
    setLibSelected(prev => prev.filter(a => a.id !== id));
  };

  const editQueuedItem = (item: PendingSend) => {
    onRemoveQueued?.(item.id);
    setText((current) => [item.text, current].filter(Boolean).join('\n'));
    const restoredFiles = item.files.map((file) => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));
    setFiles((current) => [...restoredFiles, ...current]);
    setLibSelected((current) => [
      ...item.libraryAssets,
      ...current.filter((asset) => !item.libraryAssets.some((queued) => queued.id === asset.id)),
    ]);
    onRestoreTargetSelection?.(item.previewSelection ?? null);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  // BUG-20260813-028: 剪贴板粘贴参考图直接进附件（⌘/Ctrl+V 截图或复制的图）——
  // 配合后端 refine 多模态，粘贴图会作为参考图喂给视觉模型
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) pasted.push(f);
      }
    }
    if (pasted.length) {
      e.preventDefault();
      const dt = new DataTransfer();
      pasted.forEach(f => dt.items.add(f));
      addFiles(dt.files);
    }
  };

  // 上传弹窗 → 本地上传
  const handleLocalUpload = () => {
    setShowUploadModal(false);
    fileRef.current?.click();
  };

  // 上传弹窗 → 资产库
  const handleOpenLibrary = () => {
    setShowUploadModal(false);
    setShowLibrary(true);
  };

  // 资产库选择确认
  const handleLibraryConfirm = () => {
    setLibSelected(prev => prev); // keep current selection
    setShowLibrary(false);
  };

  const toggleLibAsset = (item: CategorizedAssetItem) => {
    setLibSelected(prev =>
      prev.find(a => a.id === item.id) ? prev.filter(a => a.id !== item.id) : [...prev, item],
    );
  };

  const handleLibScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60 && !libLoading && libLoadedRef.current < libTotal) {
      fetchLibrary(libScope, libTab, libKeyword, libSubcategory, true);
    }
  };

  const handleLibSearch = (v: string) => {
    setLibKeyword(v);
    if (libDebounceRef.current) clearTimeout(libDebounceRef.current);
    libDebounceRef.current = setTimeout(() => {
      libOffsetRef.current = 0;
      libLoadedRef.current = 0;
      fetchLibrary(libScope, libTab, v, '');
    }, 300);
  };

  const handleLibSubcategoryChange = (sub: string) => {
    setLibSubcategory(sub);
    libOffsetRef.current = 0;
    libLoadedRef.current = 0;
    fetchLibrary(libScope, libTab, libKeyword, sub);
  };

  // 切换库范围：重置到对应 scope 的首个类目（与 Materials 页 scope 切换行为一致）
  const handleLibScopeChange = (scope: AssetScope) => {
    if (scope === libScope) return;
    setLibScope(scope);
    setLibTab(scope === 'public' ? PUBLIC_LIBRARY_TABS[0].key : PERSONAL_LIBRARY_TABS[0].key);
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={e => e.preventDefault()}
      className="rounded-2xl border border-border/70 bg-[hsl(var(--card)/0.85)] p-3 shadow-[0_2px_12px_rgba(0,0,0,0.12)] backdrop-blur transition-all duration-200 hover:border-border focus-within:border-primary/50 focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.08),0_4px_20px_hsl(var(--primary)/0.12)]"
    >
      {queuedItems.length > 0 && (
        <div className="mb-3 space-y-1.5 border-b border-border/50 pb-3">
          <div className="flex items-center gap-1.5 text-[11px] text-amber-300/90">
            <Clock3 size={12} />
            <span>待发送 {queuedItems.length} 条，当前任务完成后按顺序发送</span>
          </div>
          {queuedItems.map((item, index) => (
            <div key={item.id} className="flex min-h-11 items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2 py-1.5">
              <span className="w-4 shrink-0 text-center text-[10px] text-amber-300/70">{index + 1}</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border border-border/60 bg-muted/70">
                <QueuedAttachmentThumbnail item={item} />
              </span>
              <p className="min-w-0 flex-1 truncate text-xs text-foreground/85">
                {item.text || `${item.files.length + item.libraryAssets.length} 个附件`}
              </p>
              {item.previewSelection && (
                <span className="flex shrink-0 items-center gap-1 text-[10px] text-sky-300" title={item.previewSelection.label}>
                  <Crosshair size={11} />已定位
                </span>
              )}
              {(item.files.length > 0 || item.libraryAssets.length > 0) && (
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  附件 {item.files.length + item.libraryAssets.length}
                </span>
              )}
              {onRemoveQueued && (
                <>
                  <button onClick={() => editQueuedItem(item)} className="flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-muted/60 hover:text-foreground" title="移回输入框修改">
                    <CornerDownLeft size={11} />调整方向
                  </button>
                  <button onClick={() => onRemoveQueued(item.id)} className="icon-btn shrink-0 rounded p-1" title="移除待发送消息">
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Files grid — 本地上传的缩略图 */}
      {files.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-3">
          {files.map((f, i) => (
            <div key={i} className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
              {f.preview ? (
                <img src={f.preview} alt={f.file.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  <Paperclip size={16} />
                </div>
              )}
              <button
                onClick={() => removeFile(i)}
                className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X size={10} className="text-white" />
              </button>
              <span className="absolute bottom-0 inset-x-0 truncate bg-black/60 px-1 text-[8px] text-white/80">
                {f.file.name.slice(0, 10)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 已选库资源预览 */}
      {libSelected.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {libSelected.map((a) => (
            <div key={a.id} className="group relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 border-primary bg-muted">
              {a.type === 'audio' ? (
                <div className="flex h-full w-full items-center justify-center"><Music size={16} className="text-primary" /></div>
              ) : (
                <AssetImage src={a.previewUrl ?? a.url} alt={a.name} className="h-full w-full" />
              )}
              <button
                onClick={() => removeLibAsset(a.id)}
                className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X size={9} />
              </button>
              <span className="absolute bottom-0 inset-x-0 truncate bg-primary/80 px-0.5 text-center text-[7px] text-white">
                {a.name?.slice(0, 8) || '素材'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2">
        <input type="file" ref={fileRef} onChange={e => e.target.files && addFiles(e.target.files)} multiple accept="image/*,video/*" className="hidden" />

        <div className="input-neon flex-1 overflow-hidden focus-within:border-primary/60 focus-within:bg-muted/60 focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.09),0_0_18px_hsl(var(--primary)/0.16)]">
          {targetSelection && (
            <div className="px-2.5 pt-2">
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-sky-400/35 bg-sky-400/10 px-2 py-1 text-xs text-sky-300">
                <Crosshair size={13} className="shrink-0" />
                <span className="truncate">定位：{targetSelection.label}</span>
                <button onClick={onClearTargetSelection} className="ml-0.5 shrink-0 rounded p-0.5 hover:bg-sky-300/15" title="清除定向选择">
                  <X size={12} />
                </button>
              </span>
            </div>
          )}
          <textarea
            ref={taRef}
            value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste}
            disabled={disabled}
            placeholder={targetSelection ? '告诉 AI 这个位置要怎么改...' : '描述你的游戏想法... 支持拖拽/粘贴上传图片/视频/音频/文档'}
            rows={targetSelection ? 3 : 4}
            className="block w-full resize-none border-0 bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>

        <div className="flex shrink-0 gap-1">
          <button onClick={() => setShowUploadModal(true)} disabled={disabled} className="icon-btn shrink-0 rounded-lg p-2 disabled:opacity-30" title="上传附件">
            <Paperclip size={18} />
          </button>
          {(processing || busy) && onStop ? (
            <button onClick={onStop} className="icon-btn shrink-0 rounded-lg p-2" title="停止当前任务">
              <Square size={18} fill="currentColor" strokeWidth={0} />
            </button>
          ) : (
            <button onClick={handleSend} disabled={disabled || (!text.trim() && files.length === 0 && libSelected.length === 0)}
              className="icon-btn rounded-lg p-2 disabled:opacity-30" title="发送">
              <Send size={18} />
            </button>
          )}
        </div>
      </div>

      <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/90">
        {streaming && <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />}
        {processing
          ? `${processingLabel ?? '生成任务执行中'} · 点击停止可中断`
          : streaming
            ? 'Agent 响应中… 点击停止可中断'
            : 'Enter 发送 · Shift+Enter 换行 · 支持拖拽上传'}
      </p>

      {/* ===== 上传方式选择弹窗（页面居中大弹窗，Portal 渲染到 body 避免 backdrop-blur containing block 干扰）===== */}
      {showUploadModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowUploadModal(false)} />
          <div className="glass-card relative z-10 flex w-full max-w-md flex-col p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">上传文件</h3>
              <button onClick={() => setShowUploadModal(false)} className="icon-btn rounded p-1"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* 资产库 */}
              <button
                onClick={handleOpenLibrary}
                className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card/50 p-6 transition-all hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <FolderOpen size={28} className="text-primary" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">资产库</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">从资产库选择资源</div>
                </div>
              </button>
              {/* 本地上传 */}
              <button
                onClick={handleLocalUpload}
                className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card/50 p-6 transition-all hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neon-cyan/10">
                  <Upload size={28} className="text-neon-cyan" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">本地上传</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">上传图片/视频/音频</div>
                </div>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ===== 资产库弹窗（同样 Portal 渲染到 body）===== */}
      {showLibrary && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowLibrary(false)} />
          <div className="glass-card relative z-10 flex max-h-[80vh] w-full max-w-2xl flex-col p-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">资产库</h3>
              <button onClick={() => setShowLibrary(false)} className="icon-btn rounded p-1"><X size={16} /></button>
            </div>

            {/* 库范围（与真实资产库对齐：公共资产库 / 我的资产库） */}
            <div className="mb-2 flex items-center gap-1 border-b border-border/40 pb-2">
              {LIBRARY_SCOPE_TABS.map((t) => (
                <button key={t.key} onClick={() => handleLibScopeChange(t.key)}
                  className={cn('relative px-2.5 py-1 text-xs font-medium transition-colors',
                    libScope === t.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground')}>
                  {t.label}
                  {libScope === t.key && <span className="absolute bottom-[-9px] left-0 h-0.5 w-full bg-primary" />}
                </button>
              ))}
            </div>

            {/* Tab 分类（类目与真实资产库 Materials 页一致） */}
            <div className="mb-2 flex items-center gap-1">
              {(libScope === 'public' ? PUBLIC_LIBRARY_TABS : PERSONAL_LIBRARY_TABS).map((t) => (
                <button key={t.key} onClick={() => setLibTab(t.key)}
                  className={cn('flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition-colors',
                    libTab === t.key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                  <t.icon size={12} />
                  {t.label}
                </button>
              ))}
            </div>

            {/* 子分类：仅公共资产库展示（与 Materials 页一致） */}
            {libScope === 'public' && libSubcategories.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                <button onClick={() => handleLibSubcategoryChange('')}
                  className={cn('rounded px-2 py-0.5 text-[11px] transition-colors',
                    !libSubcategory ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                  全部
                </button>
                {libSubcategories.map(sub => (
                  <button key={sub} onClick={() => handleLibSubcategoryChange(sub)}
                    className={cn('rounded px-2 py-0.5 text-[11px] transition-colors',
                      libSubcategory === sub ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {/* 搜索 */}
            <div className="relative mb-3">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input className="input-neon w-full py-1.5 pl-7 pr-3 text-xs" placeholder="搜索资源..." value={libKeyword} onChange={(e) => handleLibSearch(e.target.value)} />
            </div>

            {/* 资源列表 */}
            <div className="min-h-[220px] flex-1 overflow-y-auto" onScroll={handleLibScroll}>
              {libLoading && libItems.length === 0 ? (
                <div className="flex h-52 items-center justify-center"><Loader2 size={22} className="animate-spin text-neon-cyan" /></div>
              ) : libItems.length === 0 ? (
                <div className="py-16 text-center text-xs text-muted-foreground">暂无资源</div>
              ) : (
                <div className="grid grid-cols-4 gap-2.5">
                  {libItems.map((a) => {
                    const isSel = libSelected.some(s => s.id === a.id);
                    return (
                      <div key={a.id} role="button" onClick={() => toggleLibAsset(a)}
                        className={cn('group relative cursor-pointer overflow-hidden rounded-lg border-2 text-left transition-colors',
                          isSel ? 'border-primary bg-primary/5' : 'border-transparent hover:border-border/60')}>
                        <div className="absolute left-1.5 top-1.5 z-10">
                          <span className={cn('flex h-4 w-4 items-center justify-center rounded border',
                            isSel ? 'border-primary bg-primary text-white' : 'border-white/70 bg-black/30')}>
                            {isSel && <Check size={11} />}
                          </span>
                        </div>
                        {a.type === 'audio' ? (
                          /* 音频卡片：图标 + 试听按钮 + 内联 audio 控件（与 Materials 页试听交互一致） */
                          <div className="flex aspect-square w-full flex-col items-center justify-center gap-1.5 bg-muted/40 p-1.5">
                            <Music size={20} className="text-muted-foreground/60" />
                            <button
                              onClick={(e) => { e.stopPropagation(); setLibPlayingId(libPlayingId === a.id ? null : a.id); }}
                              className="rounded-full bg-primary/10 p-1.5 text-primary hover:bg-primary/20"
                              title={libPlayingId === a.id ? '停止试听' : '试听'}>
                              {libPlayingId === a.id ? <Pause size={13} /> : <Play size={13} />}
                            </button>
                            {libPlayingId === a.id && (
                              <audio src={a.url} autoPlay controls className="w-full" style={{ height: 22 }}
                                onClick={(e) => e.stopPropagation()}
                                onEnded={() => setLibPlayingId(null)} />
                            )}
                          </div>
                        ) : (
                          <AssetImage src={a.previewUrl ?? a.url} alt={a.name} className="aspect-square w-full" />
                        )}
                        <p className="truncate px-1.5 py-1 text-center text-[10px] text-muted-foreground">{a.name || '未命名'}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {libLoading && libItems.length > 0 && (
                <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-neon-cyan/60" /></div>
              )}
            </div>

            {/* 底部操作栏 */}
            <div className="mt-3 flex items-center justify-between border-t border-border/30 pt-3">
              <span className="text-xs text-muted-foreground">
                已选 <span className="neon-text font-semibold">{libSelected.length}</span> 项 · 共 {libTotal} 个资源
              </span>
              <div className="flex gap-2">
                <button onClick={() => setShowLibrary(false)} className="rounded-lg border border-border/50 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/30">取消</button>
                <button onClick={handleLibraryConfirm} disabled={!libSelected.length}
                  className="glow-btn flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-40">
                  确认添加
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
